"use client";

import useSWR from "swr";

import SWR_KEYS from "@/constants/swr-keys";
import conversationService from "@/services/conversation.service";
import {
  AIAgentsFilter,
  ConversationFilter,
  ConversationView,
  MyConversationsFilter,
} from "@/types/conversation-filters.types";

const FILTERS_BY_VIEW: Record<ConversationView, ConversationFilter[]> = {
  [ConversationView.MY_CONVERSATIONS]: [
    MyConversationsFilter.UNREAD,
    MyConversationsFilter.OPEN,
    MyConversationsFilter.AWAITING_CLIENT,
    MyConversationsFilter.CLOSED,
  ],
  [ConversationView.AI_AGENTS]: [AIAgentsFilter.ATTENDING, AIAgentsFilter.CLOSED],
};

type Props = {
  platformId?: number | null;
  view: ConversationView;
  enabled?: boolean;
};

/**
 * Busca apenas o total de conversas de cada filtro da aba atual (pageSize 1),
 * usado para exibir os contadores ao lado dos filtros.
 */
export default function useConversationFilterCounts({ platformId, view, enabled = true }: Props) {
  const shouldFetch = enabled && !!platformId;

  const { data } = useSWR(
    shouldFetch ? [SWR_KEYS.getAllChats, "counts", platformId, view] : null,
    async () => {
      const filters = FILTERS_BY_VIEW[view];
      const results = await Promise.all(
        filters.map((filter) =>
          conversationService
            .getFilteredConversations({
              platformId: platformId!,
              view,
              filter,
              pageNumber: 1,
              pageSize: 1,
            })
            .then((response) => response?.totalCount ?? 0)
            .catch(() => 0)
        )
      );

      return filters.reduce<Partial<Record<ConversationFilter, number>>>(
        (acc, filter, index) => ({ ...acc, [filter]: results[index] }),
        {}
      );
    },
    { revalidateOnFocus: true, refreshInterval: 60000 }
  );

  return data ?? {};
}
